import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered } from "lucide-react";

// Accepts HTML or legacy plain text, always returns HTML for the editor.
export function toHtml(value) {
  if (!value) return "";
  const v = String(value);
  if (/<[a-z][\s\S]*>/i.test(v)) return v; // already HTML
  return v
    .split(/\n{2,}/)
    .map(
      (p) =>
        "<p>" +
        p.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>") +
        "</p>"
    )
    .join("");
}

const Btn = ({ active, onClick, title, children, testid }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    data-testid={testid}
    className={`w-8 h-8 flex items-center justify-center border transition-colors ${
      active ? "bg-blood text-white border-blood" : "border-[#262626] text-[#A19D94] hover:text-[#F2EBE1] hover:border-[#3a3835]"
    }`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, testid = "rich-text-editor" }) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: toHtml(value),
    immediatelyRender: false,
    editorProps: { attributes: { class: "rte-content min-h-[280px] px-3 py-2.5 focus:outline-none" } },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Sync external value changes (e.g. settings loading async) without disrupting typing.
  useEffect(() => {
    if (!editor) return;
    const incoming = toHtml(value);
    if (!editor.isFocused && incoming !== editor.getHTML()) {
      editor.commands.setContent(incoming || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="border border-[#262626] bg-[#050505]" data-testid={testid}>
      <div className="flex flex-wrap gap-1 p-1.5 border-b border-[#1a1a1a] bg-[#0A0A0A]">
        <Btn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold" testid="rte-bold"><Bold size={15} /></Btn>
        <Btn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic" testid="rte-italic"><Italic size={15} /></Btn>
        <span className="w-px bg-[#262626] mx-1 self-stretch" />
        <Btn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading" testid="rte-h2"><Heading2 size={15} /></Btn>
        <Btn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Subheading" testid="rte-h3"><Heading3 size={15} /></Btn>
        <span className="w-px bg-[#262626] mx-1 self-stretch" />
        <Btn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list" testid="rte-ul"><List size={15} /></Btn>
        <Btn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list" testid="rte-ol"><ListOrdered size={15} /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
